import { useLayoutEffect, useRef, useState } from 'react'

/**
 * What the person last did: moved focus, submitted a form, or changed a field's value. Focus that
 * a control gives back to itself after a change, as a date picker does to its field, is still part
 * of that change.
 */
let lastAction: { readonly kind: 'focus' | 'submit' | 'edit'; readonly target: EventTarget | null } = {
  kind: 'focus',
  target: null,
}

/** Whether one of the two nodes holds the other, or they are the same. */
function related(one: EventTarget | null, other: EventTarget | null): boolean {
  return one instanceof Node && other instanceof Node && (one.contains(other) || other.contains(one))
}

if (typeof document !== 'undefined') {
  document.addEventListener(
    'focusin',
    (event) => {
      if (!(lastAction.kind === 'edit' && related(lastAction.target, event.target))) {
        lastAction = { kind: 'focus', target: event.target }
      }
    },
    true,
  )
  document.addEventListener('submit', (event) => (lastAction = { kind: 'submit', target: event.target }), true)
  for (const type of ['input', 'change']) {
    document.addEventListener(type, (event) => (lastAction = { kind: 'edit', target: event.target }), true)
  }
}

/**
 * Whether focus is on the field this error describes, and the person just changed that field's
 * value, by typing or through a control's own menu or calendar.
 */
function isEditingItsField(errorId: string): boolean {
  const focused = document.activeElement
  const describedBy = focused?.getAttribute('aria-describedby')?.split(/\s+/u) ?? []
  return describedBy.includes(errorId) && lastAction.kind === 'edit' && related(lastAction.target, focused)
}

interface Shown {
  readonly element: HTMLElement
  readonly isEditing: boolean
  readonly announce: () => void
}

/** Errors shown since the last announcement, gathered so a save that shows several announces once. */
const shown = new Set<Shown>()
let isScheduled = false

/** Long enough for a form's own summary of a refused save, which may follow its fields' errors, to show. */
const gatherMs = 100

/** A form's own alert, such as "Some fields need fixing", or a save's refusal: any alert but a field's. */
const summaries = '[role=alert]:not([data-field-error])'

/** Each summary's text as the person last clicked or submitted, before what they did could change it. */
let summariesBefore = new Map<Element, string>()

function noteSummaries() {
  summariesBefore = new Map(Array.from(document.querySelectorAll(summaries), (alert) => [alert, (alert.textContent ?? '').trim()]))
}

if (typeof document !== 'undefined') {
  document.addEventListener('click', noteSummaries, true)
  document.addEventListener('submit', noteSummaries, true)
}

/**
 * Whether a summary appeared or changed since the person last clicked or submitted, so it says
 * what this attempt found, wherever it is, in the form or beside it. One left from an earlier
 * attempt says nothing new.
 */
function hasNewSummary(): boolean {
  return Array.from(document.querySelectorAll(summaries)).some((alert) => {
    const text = (alert.textContent ?? '').trim()
    return text !== '' && summariesBefore.get(alert) !== text
  })
}

function schedule(delay: number) {
  if (!isScheduled) {
    isScheduled = true
    setTimeout(announceShown, delay)
  }
}

/** A form still checking a save, such as `RecordForm` while its parts validate, which may take a request or an answer. */
const busyForm = 'form[aria-busy="true"]'
const checkingForms = new WeakSet<Element>()

/** Announces the form's errors once it is no longer busy, with the summary its check then shows. */
function waitUntilChecked(form: Element) {
  if (checkingForms.has(form)) {
    return
  }
  checkingForms.add(form)
  const watch = new MutationObserver(() => {
    if (form.getAttribute('aria-busy') !== 'true') {
      watch.disconnect()
      checkingForms.delete(form)
      schedule(0)
    }
  })
  watch.observe(form, { attributes: true, attributeFilter: ['aria-busy'] })
}

/**
 * Announces the errors shown together, once per form or dialog: one that appeared as the person
 * edited its field always; otherwise the first, unless a summary of this attempt, such as "Some
 * fields need fixing", already says what went wrong.
 */
function announceShown() {
  isScheduled = false
  const byContainer = new Map<Element, Shown[]>()
  for (const entry of shown) {
    const checking = entry.isEditing ? null : entry.element.closest(busyForm)
    if (checking !== null) {
      // Its save is still being checked; its summary, if any, comes when the check ends.
      waitUntilChecked(checking)
      continue
    }
    shown.delete(entry)
    if (entry.element.isConnected) {
      const container = entry.element.closest('form, [role=dialog], [role=alertdialog]') ?? document.body
      byContainer.set(container, [...(byContainer.get(container) ?? []), entry])
    }
  }
  const hasSummary = byContainer.size > 0 && hasNewSummary()
  for (const entries of byContainer.values()) {
    for (const entry of entries.filter((candidate) => candidate.isEditing)) {
      entry.announce()
    }
    const saved = entries.filter((candidate) => !candidate.isEditing)
    const [first] = saved.toSorted((one, other) =>
      one.element.compareDocumentPosition(other.element) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1,
    )
    if (first !== undefined && !hasSummary) {
      first.announce()
    }
  }
}

/**
 * A field's error message, in the error text color, for the field to name in `aria-describedby`.
 * A save that shows several errors at once is announced once, not once per field: by the form's
 * own alert when it has one, which then leaves each field to read its error as focus reaches it,
 * and otherwise by the first error. An error that appears or changes as the person edits its field
 * is always announced, since nothing else would say it.
 */
export function FieldError(props: { readonly id: string; readonly message: string }) {
  const { id, message } = props
  const ref = useRef<HTMLParagraphElement>(null)
  const [announced, setAnnounced] = useState<string | null>(null)

  // Once the field names this error, so whether it is being edited can be read.
  useLayoutEffect(() => {
    const element = ref.current
    if (element === null) {
      return undefined
    }
    const entry: Shown = { element, isEditing: isEditingItsField(id), announce: () => setAnnounced(message) }
    shown.add(entry)
    schedule(gatherMs)
    return () => {
      shown.delete(entry)
    }
  }, [id, message])

  return (
    <p ref={ref} id={id} className="text-xs text-error-text">
      {/* An alert inserted with its text, which every screen reader announces. */}
      {announced === message ? (
        <span role="alert" data-field-error="">
          {message}
        </span>
      ) : (
        message
      )}
    </p>
  )
}
